import IconHome from '@assets/icons/home.svg?react';
import IconInventory from '@assets/icons/inventory.svg?react';
import IconLogbook from '@assets/icons/logbook.svg?react';
import IconProfile from '@assets/icons/profile.svg?react';
import IconQrCode from '@assets/icons/qrCode.svg?react';
import useFeatureFlagOrClaim from '@hooks/useFeatureFlag';
import { useTranslator } from '@i18n';
import { Container, List } from '@mantine/core';
import { IconMap } from '@tabler/icons-react';
import { Link, type LinkProps, type RegisteredRouter } from '@tanstack/react-router';
import { themeColor } from '@util/color';
import { type ReactNode } from 'react';

export default function BottomNavigation() {
  const t = useTranslator();
  const showDashboard = useFeatureFlagOrClaim('FEATURE_DASHBOARD', 'global');

  return (
    <Container css={{ height: '100%' }}>
      <nav css={{ height: '100%' }}>
        <List
          role="menubar"
          css={{
            padding: 0,
            height: '100%',
            display: 'grid',
            gridAutoFlow: 'column',
            gridAutoColumns: 'minmax(0, 1fr)',
            justifyContent: 'space-around',
            listStyle: 'none',
          }}
        >
          {showDashboard === undefined ? null : showDashboard ? (
            <>
              <Entry to="/" icon={<IconHome />} label={t('overview.link')} />
              <Entry to="/map" icon={<IconMap />} label={t('map.link')} />
              <Entry raised to="/scanner" icon={<IconQrCode />} label={t('scanner.button')} />
              <Entry to="/inventory" icon={<IconInventory />} label={t('inventory.link')} />
              <Entry to="/profile" icon={<IconProfile />} label={t('profile.link')} />
            </>
          ) : (
            <>
              <Entry to="/map" icon={<IconMap />} label={t('map.link')} />
              <Entry to="/inventory" icon={<IconInventory />} label={t('inventory.link')} />
              <Entry raised to="/scanner" icon={<IconQrCode />} label={t('scanner.button')} />
              <Entry to="/log" icon={<IconLogbook />} label={t('log.link')} />
              <Entry to="/profile" icon={<IconProfile />} label={t('profile.link')} />
            </>
          )}
        </List>
      </nav>
    </Container>
  );
}

function Entry<To extends string>({
  raised,
  icon,
  label,
  ...props
}: LinkProps<'a', RegisteredRouter, string, To> & { raised?: boolean; icon: ReactNode; label?: string }) {
  return (
    <li
      role="presentation"
      css={[
        raised && {
          justifySelf: 'center',
        },
      ]}
    >
      <Link
        {...(props as any)}
        hash={() => ''}
        aria-label={label}
        role="menuitem"
        css={[
          {
            height: '100%',
            display: 'grid',
            justifyItems: 'center',
            alignContent: 'center',
            color: 'inherit',
            textDecoration: 'none',
            lineHeight: 1,
            gap: 4,

            '& > svg': {
              fontSize: 25,
              strokeWidth: 1.5,
            },
          },
          !raised && {
            '&.active': {
              "[data-mantine-color-scheme='dark'] &": {
                color: themeColor('primary'),
              },

              "[data-mantine-color-scheme='light'] &": {
                fontWeight: '800',

                '& > svg': {
                  fill: themeColor('primary', 3),
                },
              },
            },
          },
          raised && {
            width: 60,
            height: 60,
            color: themeColor('primary', 'contrast'),
            borderRadius: '50%',
            marginTop: -15,

            "[data-mantine-color-scheme='dark'] &": {
              background: themeColor('primary'),
            },

            "[data-mantine-color-scheme='light'] &": {
              background: themeColor('primary', 3),
            },
          },
        ]}
      >
        {icon}
        {!raised && label}
      </Link>
    </li>
  );
}
